

import React, { useState, useRef, useEffect } from 'react';
import {Box,Card,CardContent,Typography,Button,Stack,IconButton,ToggleButtonGroup,ToggleButton,CircularProgress,Alert,Divider,Chip,InputBase
} from '@mui/material';
import { ArrowBack, QrCode2, CheckCircle, Payment, Refresh, LockOpen, Lock, ReportProblem } from '@mui/icons-material';
import { orderAssignmentAPI } from './deliveryAgentAPI';
import { useNavigate } from 'react-router-dom';
import qrCodeImage from "../Logos/qrcodeScanner/qrcode.jpeg"

const PaymentCollection = ({ order, onBack, onPaymentConfirmed }) => {
  const navigate = useNavigate();
  const deliveryType = order.deliveryType || (order.orderType === 'booking' ? 'sample' : 'medicine');
  const isOnlinePayment = order.paymentMethod === 'online';
  
  const isPaymentAlreadyCollected = () => {
    if (isOnlinePayment) return true;

    if (order.orderType === 'booking' && deliveryType === 'sample') {
      if (order.paymentCollectedAt) {
        console.log(' Payment collected - has paymentCollectedAt timestamp');
        return true;
      }
      
      if (order.paymentStatus === 'paid_on_delivery' && order.paymentCollectedAt) {
        console.log(' Payment collected - paid_on_delivery with timestamp');
        return true;
      }
      
      // If status is just 'sample-collected' but no payment timestamp, payment NOT collected
      console.log(' Sample collected but payment NOT yet collected - showing payment UI');
      return false;
    }
    
    if (order.paymentStatus === 'paid_on_delivery' && order.paymentCollectedAt) return true;
    if (order.paymentStatus === 'success') return true;
    if (order.paymentStatus === 'paid') return true;
    if (order.paymentStatus === 'completed') return true;
    if (order.paymentCollectedAt) return true;
    if (order.isPaid === true && order.paymentCollectedAt) return true;
    
    // Default: not collected
    return false;
  };

  // ── OTP state ────────────────────────────────────────────────────────────
  const [otp, setOtp]                       = useState(['', '', '', '']);
  const [otpLoading, setOtpLoading]         = useState(false);
  const [otpVerified, setOtpVerified]       = useState(false);
  const [resendLoading, setResendLoading]   = useState(false);
  const [resendCooldown, setResendCooldown] = useState(10);
  const [attemptsLeft, setAttemptsLeft]     = useState(5);
  const inputRefs                           = useRef([]);
  const cooldownRef                         = useRef(null);

  // ── Payment state ────────────────────────────────────────────────────────
  const [paymentMethod, setPaymentMethod]   = useState('cash');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentDone, setPaymentDone]       = useState(isPaymentAlreadyCollected());
  const [showQR, setShowQR]                 = useState(false);

  // ── Shared ───────────────────────────────────────────────────────────────
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(false);
  const canDeliver = Boolean(order && order.id);
  const userId = sessionStorage.getItem('userId');



  useEffect(() => {
    // Check if payment is already collected on component mount ONLY
    const isAlreadyCollected = isPaymentAlreadyCollected();
    
    if (isAlreadyCollected && !paymentDone) {
      console.log(' Payment already collected on mount, skipping payment UI');
      setPaymentDone(true);
    }
  }, []);

  // Report issue handler
  const handleReportIssue = () => {
    console.log(' Reporting issue for order:', order);
    
    const normalizedOrder = {
      id: order.id,
      orderId: order.orderId || order.bookingId,
      orderType: order.orderType || 'booking',
      deliveryType: deliveryType,
      customerName: order.customerName,
      customerPhone: order.customerPhone,
      customerEmail: order.customerEmail,
      customerAddress: order.customerAddress,
      serviceName: order.serviceName,
      totalAmount: order.totalAmount,
      orderStatus: order.orderStatus,
      deliveryStatus: order.deliveryStatus,
      paymentMethod: order.paymentMethod,
      isPaid: order.isPaid
    };

    navigate('/delivery-report-issue', {
      state: { order: normalizedOrder }
    });
  };

  // Cooldown timer
  useEffect(() => {
    startCooldown();
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  const startCooldown = () => {
    setResendCooldown(10);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // Auto-focus first OTP box
  useEffect(() => {
    if (!otpVerified && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [otpVerified]);

  // OTP HANDLERS
  const handleOTPChange = (index, value) => {
    if (value.length > 1) return;
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError(null);
    if (value && index < 3) inputRefs.current[index + 1]?.focus();
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const newOtp = [...otp];
      newOtp[index - 1] = '';
      setOtp(newOtp);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (!pasted.length) return;
    const newOtp = ['', '', '', ''];
    pasted.split('').forEach((ch, i) => { newOtp[i] = ch; });
    setOtp(newOtp);
    setError(null);
    inputRefs.current[Math.min(pasted.length, 3)]?.focus();
  };

  const handleVerifyOTP = async () => {
    const otpValue = otp.join('');
    if (otpValue.length !== 4) { setError('Please enter all 4 digits'); return; }
    try {
      setOtpLoading(true);
      setError(null);
      
      const response = await orderAssignmentAPI.verifyDeliveryOTP(
        order.id,
        order.orderType || 'booking',
        otpValue,
        deliveryType
      );
      
      if (response.data.success) {
        setOtpVerified(true);
        console.log(' OTP verified successfully for', deliveryType);
        //  DON'T set paymentDone here - let user select payment method
      } else {
        setError(response.data.message || 'Invalid OTP');
        if (response.data.attemptsLeft !== undefined) setAttemptsLeft(response.data.attemptsLeft);
        setOtp(['', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('OTP verification error:', err);
    
      let msg = 'Error verifying OTP';
      let remaining = null;
    
      if (err.response?.data) {
        msg = err.response.data.message || msg;
        remaining = err.response.data.attemptsLeft;
      }
    
      setError(msg);
    
      if (remaining !== null) {
        setAttemptsLeft(remaining);
      }
    
      if (msg.toLowerCase().includes('locked')) {
        setAttemptsLeft(0);
      }
    
      setOtp(['', '', '', '']);
      inputRefs.current[0]?.focus();
    }
    
    finally {
      setOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    try {
      setResendLoading(true);
      setError(null);
      
      const response = await orderAssignmentAPI.resendDeliveryOTP(
        order.id,
        order.orderType || 'booking',
        deliveryType
      );
      
      if (response.data.success) {
        setOtp(['', '', '', '']);
        setAttemptsLeft(5);
        startCooldown();
        inputRefs.current[0]?.focus();
        console.log(' OTP resent successfully for', deliveryType);
      } else {
        setError(response.data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      console.error(' OTP resend error:', err);
      setError(err.response?.data?.message || 'Error resending OTP');
    } finally {
      setResendLoading(false);
    }
  };

  //  FIXED: Added setPaymentDone(true) to update state
  const handleConfirmPayment = async () => {
    try {
      setPaymentLoading(true);
      setError(null);
      const response = await orderAssignmentAPI.confirmPayment(
        userId,
        order.id,
        order.orderType || 'booking',
        {
          paymentMethod,
          amount: order.totalAmount,
          collectedAt: new Date().toISOString(),
          deliveryType: deliveryType
        }
      );
      if (response.data.success) {
        setPaymentDone(true);
        console.log(' Payment confirmed:', response.data);
      } else {
        setError(response.data.message || 'Payment confirmation failed');
      }
    } catch (err) {
      console.error(' Payment confirmation error:', err);
      setError(err.response?.data?.message || 'Error confirming payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleProceed = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Just call the parent callback - payment should already be confirmed
      await onPaymentConfirmed();
      
    } catch (err) {
      console.error(' Error in handleProceed:', err);
      setError(err.response?.data?.message || 'Error proceeding to next step');
    } finally {
      setLoading(false);
    }
  };

  // DERIVED FLAGS
  const isOTPComplete  = otp.every((d) => d !== '');
  const isOTPLocked    = attemptsLeft <= 0;
  const canProceed     = otpVerified && (isOnlinePayment || paymentDone);

  // ✅DEBUG: Log state changes
  useEffect(() => {
    console.log('🔍 State Update:', {
      otpVerified,
      isOnlinePayment,
      paymentDone,
      canProceed
    });
  }, [otpVerified, isOnlinePayment, paymentDone, canProceed]);

  // RENDER
  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>

      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <IconButton onClick={onBack} disabled={otpLoading || paymentLoading || loading}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" fontWeight={600}>
          {!otpVerified
            ? `Verify ${deliveryType === 'report' ? 'Report Delivery' : deliveryType === 'medicine' ? 'Delivery' : 'Sample Collection'} OTP`
            : !isOnlinePayment && !paymentDone
              ? 'Collect Payment'
              : 'Ready to Proceed'}
        </Typography>
      </Stack>

      {/* Error banner */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Order summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>Order Summary</Typography>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Order ID:</Typography>
            <Typography variant="body2" fontWeight={500}>{order.orderId}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Customer:</Typography>
            <Typography variant="body2" fontWeight={500}>{order.customerName}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Service Type:</Typography>
            <Chip 
              label={deliveryType === 'report' ? 'Report Delivery' : deliveryType === 'medicine' ? 'Medicine Delivery' : 'Sample Collection'} 
              size="small" 
              color="secondary" 
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Status:</Typography>
            <Chip label={order.deliveryStatus || order.orderStatus} size="small" color="info" />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
            <Chip
              label={isOnlinePayment ? 'ONLINE' : 'OFFLINE (Cash / UPI / Card)'}
              size="small"
              color={isOnlinePayment ? 'success' : 'warning'}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Amount card */}
      <Card sx={{ mb: 3, bgcolor: '#006D77', color: '#fff' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ opacity: 0.9 }}>Order Amount</Typography>
              <Typography variant="h3" fontWeight={700}>₹{order.totalAmount}</Typography>
            </Box>
            <Payment sx={{ fontSize: 60, opacity: 0.3 }} />
          </Stack>
        </CardContent>
      </Card>

      {/* STEP 1 — OTP VERIFICATION */}
      {!otpVerified && (
        <>
          <Card sx={{ mb: 3, bgcolor: '#006D77', color: '#fff' }}>
            <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 1.5
              }}>
                <LockOpen sx={{ fontSize: 28, color: '#fff' }} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Enter {deliveryType === 'report' ? 'Report Delivery' : deliveryType === 'medicine' ? 'Delivery' : 'Sample Collection'} OTP
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                Ask the customer for the 4-digit OTP to verify {deliveryType === 'report' ? 'report delivery' : deliveryType === 'medicine' ? 'delivery' : 'sample collection'}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Enter 4-digit OTP
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, mb: 1 }}>
                {otp.map((digit, index) => (
                  <InputBase
                    key={index}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    value={digit}
                    onChange={(e) => handleOTPChange(index, e.target.value)}
                    onKeyDown={(e) => handleOTPKeyDown(index, e)}
                    onPaste={handleOTPPaste}
                    onFocus={(e) => e.target.select()}
                    inputProps={{
                      maxLength: 1,
                      style: { textAlign: 'center' },
                      inputMode: 'numeric',
                      autoComplete: 'one-time-code'
                    }}
                    disabled={otpLoading || isOTPLocked}
                    sx={{
                      width: 56, height: 64,
                      border: `2px solid ${digit ? '#006D77' : '#ddd'}`,
                      borderRadius: 2,
                      bgcolor: digit ? '#f0fafa' : '#fff',
                      transition: 'all 0.2s',
                      '&:focus-within': { borderColor: '#006D77', boxShadow: '0 0 0 3px rgba(0,109,119,0.2)', bgcolor: '#f0fafa' },
                      '&.Mui-disabled': { bgcolor: '#f5f5f5', borderColor: '#e0e0e0' },
                      '& input': { p: 0, fontSize: 28, fontWeight: 700, color: '#006D77' }
                    }}
                  />
                ))}
              </Box>

              {attemptsLeft < 5 && attemptsLeft > 0 && (
                <Typography variant="caption" sx={{ color: attemptsLeft <= 2 ? '#dc3545' : '#856404', display: 'block', mt: 1 }}>
                  {attemptsLeft} attempt{attemptsLeft !== 1 ? 's' : ''} remaining
                </Typography>
              )}
              {isOTPLocked && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                  <Lock sx={{ fontSize: 16, color: '#dc3545' }} />
                  <Typography variant="caption" color="#dc3545">OTP locked. Please resend a new OTP.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Button
              variant="text"
              startIcon={resendLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={handleResendOTP}
              disabled={resendLoading || resendCooldown > 0}
              sx={{ color: '#006D77', fontWeight: 600, '&:hover': { color: '#005561' }, '&.Mui-disabled': { color: '#aaa' } }}
            >
              {resendLoading ? 'Resending...' : resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
            </Button>
          </Box>

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleVerifyOTP}
            disabled={otpLoading || !isOTPComplete || isOTPLocked}
            startIcon={otpLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
            sx={{ py: 1.5, mb: 3, bgcolor: '#006D77', '&:hover': { bgcolor: '#005561' }, '&.Mui-disabled': { bgcolor: '#ccc' } }}
          >
            {otpLoading ? 'Verifying...' : 'VERIFY OTP'}
          </Button>
        </>
      )}
      {error && (
        <Typography color="error" sx={{ mt: 1 }}>
        {error}
        </Typography>

        )}

      {/* OTP verified badge */}
      {otpVerified && (
        <Card sx={{ mb: 3, border: '1px solid #c3e6cb', bgcolor: '#d4edda' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CheckCircle sx={{ color: '#28a745', fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} color="#155724">✅ OTP Verified</Typography>
                <Typography variant="caption" color="#155724">Customer identity confirmed</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* STEP 2 — PAYMENT */}
      {otpVerified && (
        <>
          {/* ONLINE OR ALREADY COLLECTED: Show confirmation */}
          {(isOnlinePayment || paymentDone) && (
            <Card sx={{ mb: 3, border: '1px solid #c3e6cb', bgcolor: '#d4edda' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <CheckCircle sx={{ color: '#28a745', fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} color="#155724">
                      ✅ Payment {isOnlinePayment ? 'Completed (Online)' : order.paymentStatus === 'paid_on_delivery' ? 'Already Collected (COD)' : 'Confirmed'}
                    </Typography>
                    <Typography variant="caption" color="#155724">
                      ₹{order.totalAmount} {isOnlinePayment ? 'was paid online' : order.paymentStatus === 'paid_on_delivery' ? 'was already collected on delivery' : 'collected successfully'}. No action needed.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* OFFLINE + NOT YET COLLECTED: Show payment collection UI */}
          {!isOnlinePayment && !paymentDone && (
            <>
              {console.log('🔵 Showing payment collection UI:', {
                isOnlinePayment,
                paymentDone,
                paymentMethod: order.paymentMethod,
                paymentStatus: order.paymentStatus,
                shouldShow: !isOnlinePayment && !paymentDone
              })}
              
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight={600}>Select Payment Method</Typography>
                  <ToggleButtonGroup
                    value={paymentMethod}
                    exclusive
                    onChange={(e, val) => {
                      if (val) { setPaymentMethod(val); if (val !== 'upi') setShowQR(false); }
                    }}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    {['cash', 'upi', 'card'].map((method) => (
                      <ToggleButton
                        key={method}
                        value={method}
                        sx={{
                          py: 2,
                          '&.Mui-selected': { bgcolor: '#006D77', color: '#fff', '&:hover': { bgcolor: '#005561' } }
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body1" fontWeight={600}>
                            {method === 'cash' ? 'CASH' : method === 'upi' ? 'UPI' : 'CARD'}
                          </Typography>
                          <Typography variant="caption">
                            {method === 'cash' ? 'Pay in cash' : method === 'upi' ? 'Scan QR code' : 'POS machine'}
                          </Typography>
                        </Box>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </CardContent>
              </Card>

              {paymentMethod === 'upi' && (
                <Card sx={{ mb: 3 }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Button
                      startIcon={<QrCode2 />}
                      onClick={() => setShowQR(!showQR)}
                      variant="outlined"
                      fullWidth
                      disabled={paymentLoading}
                    >
                      {showQR ? 'Hide QR Code' : 'Show QR Code'}
                    </Button>
                    {showQR && (
                      <Box mt={3}>
                        <img
                        src={qrCodeImage}
                          // src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=upi://pay?pa=merchant@upi&pn=Ayuja&am=${order.totalAmount}&cu=INR`}
                          alt="UPI QR"
                          style={{ width: 250, height: 250, border: '2px solid #ddd', borderRadius: 8 }}
                        />
                        <Typography variant="caption" display="block" mt={2} color="text.secondary">
                          Ask customer to scan this QR code with any UPI app
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Payment Collection Checklist:</Typography>
                  <Typography variant="body2" component="div">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>Verify the payment amount: ₹{order.totalAmount}</li>
                      <li>Collect payment using selected method</li>
                      {paymentMethod === 'upi'  && <li>Wait for payment confirmation</li>}
                      {paymentMethod === 'cash' && <li>Count cash and provide change if needed</li>}
                      {paymentMethod === 'card' && <li>Complete POS transaction</li>}
                      <li>Confirm payment received before proceeding</li>
                    </ul>
                  </Typography>
                </CardContent>
              </Card>

              {/* ✅ FIXED: This button now properly sets paymentDone=true */}
              <Button
                variant="contained"
                color="success"
                size="large"
                fullWidth
                onClick={handleConfirmPayment}
                disabled={paymentLoading}
                startIcon={
                  paymentLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />
                }
                sx={{ py: 1.5, mb: 2, bgcolor: '#28a745', '&:hover': { bgcolor: '#218838' } }}
              >
                {paymentLoading ? 'Processing...' : 'CONFIRM PAYMENT COLLECTED'}
              </Button>
            </>
          )}
        </>
      )}

      {/* STEP 3 — PROCEED BUTTON (only if payment is done) */}
      {canProceed && (
        <>
          <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                ✅ All verification complete:
              </Typography>
              <Typography variant="body2" component="div">
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>✅ OTP verified with customer</li>
                  <li>✅ {isOnlinePayment ? 'Payment completed online' : 'Payment collected'}</li>
                  <li>
                    Ready to {
                      deliveryType === 'report' 
                        ? 'confirm report delivery'
                        : order.orderType === 'medicine' 
                          ? 'confirm medicine delivery' 
                          : 'proceed to lab transfer'
                    }
                  </li>
                </ul>
              </Typography>
            </CardContent>
          </Card>

          <Button
            variant="contained"
            color="success"
            size="large"
            fullWidth
            onClick={handleProceed}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
            sx={{ py: 1.5, mb: 2, bgcolor: '#28a745', '&:hover': { bgcolor: '#218838' } }}
          >
            {loading 
              ? 'Processing...' 
              : deliveryType === 'report'
                ? 'CONFIRM REPORT DELIVERY'
                : order.orderType === 'medicine' 
                  ? 'CONFIRM MEDICINE DELIVERY'
                  : 'PROCEED TO LAB TRANSFER'
            }
          </Button>
        </>
      )}

      {/* Action Buttons */}
      <Stack spacing={2}>
        <Button
          variant="outlined"
          size="large"
          fullWidth
          onClick={onBack}
          disabled={otpLoading || paymentLoading || loading}
          sx={{ py: 1.5 }}
        >
          Cancel
        </Button>

        {canDeliver && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleReportIssue}
            disabled={loading}
            startIcon={<ReportProblem />}
            fullWidth
            sx={{ py: 1.5 }}
          >
            Report Issue
          </Button>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
        {!otpVerified
          ? 'Please verify OTP first before collecting payment'
          : !isOnlinePayment && !paymentDone
            ? 'Collect payment after OTP verification'
            : deliveryType === 'report'
              ? 'Both steps completed – ready to confirm report delivery'
              : order.orderType === 'medicine'
                ? 'Both steps completed – ready to confirm medicine delivery'
                : 'Both steps completed – ready to proceed to lab transfer'}
      </Typography>
    </Box>
  );
};

export default PaymentCollection;